import { useId, useMemo } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { CodeFileInfo, JobInfoDTO } from "../../../serviceapi/jobRunnerApi";

const NEW_EMPTY_FILE_ID = -1;

interface CodeAssignProps {
  isCodeExist: boolean;
  codeFilesList: CodeFileInfo[];
  selectedCodeFile: CodeFileInfo | null;
  setSelectedCodeFile: (selectedCodeFile: CodeFileInfo | null) => void;
  setFileToUpload: (fileToUpload: File | null) => void;
  loadTaskCode: () => void;
  selectedJob: JobInfoDTO | null;
  uploadTaskCode: () => void;
  setIsCreateFileDialogVisible: (isCreateFileDialogVisible: boolean) => void;
  createEmptyFileForJob: () => void;
}

const CodeAssign = ({
  isCodeExist,
  codeFilesList,
  selectedCodeFile,
  setSelectedCodeFile,
  setFileToUpload,
  loadTaskCode,
  selectedJob,
  uploadTaskCode,
  setIsCreateFileDialogVisible,
  createEmptyFileForJob,
}: CodeAssignProps) => {
  const id = useId();
  const dropdownOptions = useMemo(() => {
    const newEmptyEntry: CodeFileInfo = {
      id: NEW_EMPTY_FILE_ID,
      name: `+ New empty file (${selectedJob?.taskId ?? "unnamed"}.py)`,
      mimeType: "text/x-python",
      uniqueId: id,
    };
    return [newEmptyEntry, ...codeFilesList];
  }, [codeFilesList, selectedJob, id]);

  const handleSelect = () => {
    if (selectedCodeFile?.id === NEW_EMPTY_FILE_ID) {
      createEmptyFileForJob();
    } else {
      loadTaskCode();
    }
  };

  return (
    <div
      className={`flex flex-column gap-3 mx-auto ${
        !isCodeExist
          ? "border-round-lg shadow-2 mt-5 w-20rem p-4"
          : "w-19rem p-1"
      }  surface-card`}
    >
      {!isCodeExist && (
        <h3 className="text-xl mb-0 font-semibold text-center">
          No Code Assigned
        </h3>
      )}
      <p className="text-sm text-gray-200 text-center">
        Please select an existing code, upload a new one, or create a blank
        Python file.
      </p>
      <div className="flex flex-column gap-2">
        <label className="font-medium">Select a code file</label>
        <Dropdown
          value={selectedCodeFile}
          options={dropdownOptions}
          className="w-full"
          onChange={(e) => setSelectedCodeFile(e.value)}
          optionLabel="name"
          placeholder="Choose a code file..."
        />
        <Button
          label="Select"
          disabled={!selectedCodeFile}
          onClick={handleSelect}
          className="p-button-primary"
        />
      </div>
      <div className="flex align-items-center gap-2">
        <div className="w-full border-top-1 border-gray-300"></div>
        <span className="text-gray-300 text-sm">OR</span>
        <div className="w-full border-top-1 border-gray-300"></div>
      </div>
      <div className="flex flex-column gap-2">
        <label className="font-medium">Upload a new Python file</label>
        <div className="flex flex-row justify-content-between align-items-center">
          <input
            type="file"
            accept=".py"
            onChange={(e) => setFileToUpload(e.target.files?.[0] || null)}
          />
          <Button
            className="p-button-primary scheduler-button"
            icon="pi pi-upload"
            tooltip="Upload Python Code"
            onClick={uploadTaskCode}
            disabled={!selectedJob}
          />
        </div>
      </div>
      <div className="flex align-items-center gap-2">
        <div className="w-full border-top-1 border-gray-300"></div>
        <span className="text-gray-300 text-sm">OR</span>
        <div className="w-full border-top-1 border-gray-300"></div>
      </div>
      <Button
        className="p-button-outlined p-button-primary w-full"
        icon="pi pi-code"
        label="Create Blank Python File"
        tooltip="Generate a new Python code file"
        onClick={() => setIsCreateFileDialogVisible(true)}
      />
    </div>
  );
};

export default CodeAssign;
